import React, { useMemo, useRef, useState } from "react";
import {
    Popover,
    Box,
    Typography,
    Button,
    IconButton,
    ButtonBase,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Tooltip,
} from "@mui/material";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import { colors, lightPalette, darkPalette } from "@/theme/colors";
import filterTagData from "@/data/filterTagData.json";

// ════════════════════════════════════════════════════════════════════════════
// ColorPicker
// ════════════════════════════════════════════════════════════════════════════

export type ColorPickerColor = string | null;

export interface ColorPickerProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    /** Called with the chosen color, or null to reset to default. */
    onSelect: (color: ColorPickerColor) => void;
    /** Currently applied color (rendered with selection ring). */
    currentColor?: ColorPickerColor;
    title?: string;
    /** Label for the reset/remove button. Defaults to "Remove color". */
    removeLabel?: string;
    /**
     * When set, indicates the picker was opened to edit a recent-color
     * hotkey slot. Used to (a) auto-default the remove button label to
     * "Remove color hotkey N" (where N matches the on-screen key label,
     * 1–9 then 0) when no explicit `removeLabel` is supplied, and (b)
     * surface the index to consumers via the prop itself. Removal of the
     * hotkey is signalled to the parent via the existing `onSelect(null)`
     * callback — the parent owns the slot list and the shift behaviour.
     */
    hotkeyIndex?: number;
}

// Hue order per spec: rose, red, orange, yellow, green, blue, purple
const HUES: Array<keyof typeof colors> = [
    "rose",
    "red",
    "orange",
    "yellow",
    "green",
    "blue",
    "purple",
];
// Row order: light (lightPalette), primary (colors[hue].primary), dark (darkPalette)
const SHADES: Array<"light" | "primary" | "dark"> = [
    "light",
    "primary",
    "dark",
];

// Resolve a hue+shade to the high-contrast palette value.
function resolveColor(
    hue: keyof typeof colors,
    shade: "light" | "primary" | "dark",
): string {
    if (shade === "primary") {
        return (colors[hue] as Record<string, string>).primary;
    }
    if (shade === "light") return lightPalette[hue as string];
    return darkPalette[hue as string];
}

// Neutral column values, aligned with SHADES rows.
const NEUTRALS: Record<"light" | "primary" | "dark", string> = {
    light: "#ffffff",
    primary: "#9e9e9e",
    dark: "#000000",
};

export function ColorPicker({
    anchorEl,
    open,
    onClose,
    onSelect,
    currentColor,
    title,
    removeLabel,
    hotkeyIndex,
}: ColorPickerProps) {
    // Keys 1–9 then 0 mirror the keyboard layout used by the recent-color
    // shortcuts. Compute the on-screen digit label from the slot index.
    const hotkeyDigit =
        typeof hotkeyIndex === "number"
            ? hotkeyIndex === 9
                ? 0
                : hotkeyIndex + 1
            : null;
    const resolvedRemoveLabel =
        removeLabel ??
        (hotkeyDigit !== null
            ? `Remove color hotkey ${hotkeyDigit}`
            : "Remove color");
    const handleSwatchClick = (color: string) => {
        onSelect(color);
        onClose();
    };
    const handleRemove = () => {
        onSelect(null);
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            slotProps={{
                paper: {
                    sx: { p: 1.25, borderRadius: 2 },
                    onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
                },
            }}
        >
            {title && (
                <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                    {title}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${HUES.length + 1}, 28px)`,
                    gridAutoRows: "28px",
                    gap: "6px",
                }}
            >
                {SHADES.map((shade) => {
                    const neutralValue = NEUTRALS[shade];
                    const neutralActive =
                        currentColor &&
                        neutralValue.toLowerCase() ===
                            currentColor.toLowerCase();
                    return (
                        <React.Fragment key={`row-${shade}`}>
                            {HUES.map((hue) => {
                                const value = resolveColor(hue, shade);
                                const isActive =
                                    currentColor &&
                                    value.toLowerCase() ===
                                        currentColor.toLowerCase();
                                return (
                                    <Box
                                        key={`${hue}-${shade}`}
                                        component="button"
                                        type="button"
                                        title={`${hue} ${shade}`}
                                        onMouseDown={(e: React.MouseEvent) =>
                                            e.preventDefault()
                                        }
                                        onClick={() => handleSwatchClick(value)}
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            background: value,
                                            cursor: "pointer",
                                            border: isActive
                                                ? "2px solid #000"
                                                : "1px solid rgba(0,0,0,0.15)",
                                            boxShadow: isActive
                                                ? "0 0 0 2px #fff inset"
                                                : "none",
                                            padding: 0,
                                            "&:hover": {
                                                outline:
                                                    "1px solid rgba(0,0,0,0.4)",
                                            },
                                        }}
                                    />
                                );
                            })}
                            <Box
                                key={`neutral-${shade}`}
                                component="button"
                                type="button"
                                title={`neutral ${shade}`}
                                onMouseDown={(e: React.MouseEvent) =>
                                    e.preventDefault()
                                }
                                onClick={() => handleSwatchClick(neutralValue)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    background: neutralValue,
                                    cursor: "pointer",
                                    border: neutralActive
                                        ? "2px solid #1976d2"
                                        : "1px solid rgba(0,0,0,0.25)",
                                    boxShadow: neutralActive
                                        ? "0 0 0 2px #fff inset"
                                        : "none",
                                    padding: 0,
                                    "&:hover": {
                                        outline: "1px solid rgba(0,0,0,0.4)",
                                    },
                                }}
                            />
                        </React.Fragment>
                    );
                })}
            </Box>
            <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    onClick={handleRemove}
                    startIcon={<FormatColorResetIcon fontSize="small" />}
                    sx={{ textTransform: "none" }}
                >
                    {resolvedRemoveLabel}
                </Button>
            </Box>
        </Popover>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// TimePicker
// ════════════════════════════════════════════════════════════════════════════

export type TimeValue = string; // HH:mm (24h)

export interface TimePickerProps {
    label?: string;
    value?: TimeValue;
    onChange: (value: TimeValue) => void;
    /** @deprecated Ignored – custom picker uses 5-minute steps */
    stepMinutes?: number;
    fullWidth?: boolean;
    disabled?: boolean;
    id?: string;
    clearable?: boolean;
    placeholder?: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Parse HH:mm (24h) → { hour12, minute, ampm } */
const parseTime = (hhmm?: string) => {
    if (!hhmm) return { hour12: 12, minute: 0, ampm: "AM" as const };
    const [hStr, mStr] = hhmm.split(":");
    const h24 = parseInt(hStr || "0", 10);
    const m = parseInt(mStr || "0", 10);
    const ampm: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
    const hour12 = h24 % 12 || 12;
    return { hour12, minute: m, ampm };
};

/** Build HH:mm from hour12 + minute + ampm */
const buildTime = (hour12: number, minute: number, ampm: "AM" | "PM") => {
    let h24 = hour12 % 12;
    if (ampm === "PM") h24 += 12;
    return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

/** Format HH:mm → h:mm AM/PM for display */
const to12Hour = (hhmm?: string) => {
    if (!hhmm) return "";
    const { hour12, minute, ampm } = parseTime(hhmm);
    return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55] as const;

export function TimePicker({
    label,
    value,
    onChange,
    fullWidth = true,
    disabled,
    id,
    clearable,
    placeholder = "Select time",
}: TimePickerProps) {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);

    const parsed = parseTime(value);

    const setHour = (h: number) =>
        onChange(buildTime(h, parsed.minute, parsed.ampm));
    const setMinute = (m: number) =>
        onChange(buildTime(parsed.hour12, m, parsed.ampm));
    const setAmPm = (ap: "AM" | "PM") =>
        onChange(buildTime(parsed.hour12, parsed.minute, ap));

    return (
        <Box
            ref={anchorRef}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: fullWidth ? "100%" : "auto",
            }}
        >
            {/* Visible trigger button */}
            <Box
                id={id}
                onClick={disabled ? undefined : () => setOpen(true)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    border: "1px solid",
                    borderColor: disabled ? "action.disabled" : "divider",
                    borderRadius: 1,
                    cursor: disabled ? "default" : "pointer",
                    width: fullWidth ? "100%" : "auto",
                    minWidth: 160,
                    backgroundColor: disabled
                        ? "action.disabledBackground"
                        : "background.paper",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:hover": disabled
                        ? {}
                        : {
                              borderColor: "primary.main",
                              boxShadow: (theme: any) =>
                                  `0 0 0 1px ${theme.palette.primary.main}`,
                          },
                }}
            >
                <AccessTimeIcon
                    fontSize="small"
                    sx={{
                        color: disabled ? "action.disabled" : "action.active",
                    }}
                />
                <Box>
                    {label && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                lineHeight: 1.2,
                                display: "block",
                            }}
                        >
                            {label}
                        </Typography>
                    )}
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 500,
                            color: value ? "text.primary" : "text.secondary",
                        }}
                    >
                        {value ? to12Hour(value) : placeholder}
                    </Typography>
                </Box>
                {clearable && value && !disabled && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange("");
                        }}
                        aria-label="Clear time"
                        sx={{ ml: "auto" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* Custom 5-minute-increment picker popover */}
            <Popover
                open={open && !disabled}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        boxShadow: 6,
                        overflow: "hidden",
                    },
                }}
            >
                <Box sx={{ display: "flex", userSelect: "none" }}>
                    {/* Hours column */}
                    <TimeColumn label="Hour">
                        {HOURS.map((h) => (
                            <TimeCell
                                key={h}
                                selected={!!value && parsed.hour12 === h}
                                onClick={() => setHour(h)}
                            >
                                {h}
                            </TimeCell>
                        ))}
                    </TimeColumn>

                    {/* Minutes column – strictly 5-minute increments */}
                    <TimeColumn label="Min">
                        {MINUTES.map((m) => (
                            <TimeCell
                                key={m}
                                selected={!!value && parsed.minute === m}
                                onClick={() => setMinute(m)}
                            >
                                {String(m).padStart(2, "0")}
                            </TimeCell>
                        ))}
                    </TimeColumn>

                    {/* AM / PM column */}
                    <TimeColumn label="AM/PM">
                        {(["AM", "PM"] as const).map((ap) => (
                            <TimeCell
                                key={ap}
                                selected={!!value && parsed.ampm === ap}
                                onClick={() => setAmPm(ap)}
                            >
                                {ap}
                            </TimeCell>
                        ))}
                    </TimeColumn>
                </Box>
            </Popover>
        </Box>
    );
}

// ─── sub-components ──────────────────────────────────────────────────────────

function TimeColumn({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                borderRight: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderRight: "none" },
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "text.secondary",
                    px: 1.5,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                    letterSpacing: 0.5,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    overflowY: "auto",
                    maxHeight: 220,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "grey.300",
                        borderRadius: 2,
                    },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function TimeCell({
    selected,
    onClick,
    children,
}: {
    selected: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                display: "block",
                width: "100%",
                textAlign: "center",
                px: 2.5,
                py: 0.75,
                fontFamily: "inherit",
                fontSize: "0.875rem",
                fontWeight: selected ? 700 : 400,
                bgcolor: selected ? "primary.main" : "transparent",
                color: selected ? "primary.contrastText" : "text.primary",
                "&:hover": {
                    bgcolor: selected ? "primary.dark" : "action.hover",
                },
                transition: "background-color 0.15s",
            }}
        >
            {children}
        </ButtonBase>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// RegionPicker
// ════════════════════════════════════════════════════════════════════════════
//
// Toggleable super-region chips (North America, Europe, …) sourced from
// `data/filterTagData.json`. When a super-region is toggled ON, a modal opens
// that lets the user pick the specific child regions/countries that apply.
// The component stays string-list–compatible with the existing region-tag
// stores: `value` is an array of region display names that mixes super-region
// names (e.g. "North America") with child-region names (e.g. "Canada",
// "USA", "Mexico"). Turning a super-region OFF removes both the super-region
// and every one of its child names from the value.

// Third (leaf) level — member states that live under an expandable child
// region (e.g. the 27 EU member states under "European Union", or Iceland/
// Liechtenstein/Norway/Switzerland under "European Free Trade Association").
type RegionPickerLeafRegion = {
    id: string;
    name: string;
    abbrev?: string;
};

type RegionPickerSubRegion = {
    id: string;
    name: string;
    abbrev?: string;
    /** Optional drill-down level. When present (and non-placeholder), the
     *  child renders as an expandable group inside the picker modal. */
    subRegions?: RegionPickerLeafRegion[];
};

type RegionPickerSuperRegion = {
    id: string;
    name: string;
    regions: RegionPickerSubRegion[];
};

const SUPER_REGIONS: RegionPickerSuperRegion[] = (
    filterTagData.regions as RegionPickerSuperRegion[]
).map((sr) => ({
    id: sr.id,
    name: sr.name,
    regions: (sr.regions || []).map((r) => ({
        id: r.id,
        name: r.name,
        abbrev: (r as any).abbrev,
        subRegions: ((r as any).subRegions || []).map(
            (s: RegionPickerLeafRegion) => ({
                id: s.id,
                name: s.name,
                abbrev: s.abbrev,
            }),
        ),
    })),
}));

// Some super-regions (e.g. "Global") only contain the placeholder "ALL" entry.
// Treat those as having no real children — skip the picker modal entirely.
function realChildren(sr: RegionPickerSuperRegion): RegionPickerSubRegion[] {
    return sr.regions.filter((r) => r.id !== "ALL");
}

// Real (non-placeholder) leaf regions of a child. A child with one or more of
// these renders as an expandable group; an empty list means it's a plain
// toggle chip (the historical 2-level behaviour).
function realSubRegions(child: RegionPickerSubRegion): RegionPickerLeafRegion[] {
    return (child.subRegions || []).filter((s) => s.id !== "ALL");
}

// Leaf regions are matched by canonical NAME only — never by abbreviation.
// Their abbreviations are short ISO-style codes (e.g. "IS", "NO", "CA") that
// collide across regions ("CA" = California vs Canada, "IS" = Iceland), so we
// avoid the same ambiguity localStore.ts already guards against.
function leafNameLower(leaf: RegionPickerLeafRegion): string {
    return leaf.name.trim().toLowerCase();
}

const NAME_TO_SUPER = new Map<string, RegionPickerSuperRegion>(
    SUPER_REGIONS.map((sr) => [sr.name.toLowerCase(), sr]),
);

// All names that should be considered an alias of a child region. Includes
// the canonical display name and the country/region abbreviation (e.g. USA
// ↔ "US", UK ↔ "GB", etc.) so that legacy stored values like "US" are
// recognised by the picker as the same selection as toggling "USA" on.
function childAliasesLower(child: RegionPickerSubRegion): string[] {
    const out: string[] = [child.name.trim().toLowerCase()];
    const ab = (child.abbrev || "").trim();
    if (ab && ab !== "-" && ab.toLowerCase() !== child.name.toLowerCase()) {
        out.push(ab.toLowerCase());
    }
    return out;
}

function childNamesLowerOf(sr: RegionPickerSuperRegion): Set<string> {
    // Include both canonical names and abbreviations so that turning a
    // super-region off also strips any legacy abbreviation entries
    // (e.g. removes "US" when the user toggles USA's parent off).
    const set = new Set<string>();
    realChildren(sr).forEach((r) => {
        childAliasesLower(r).forEach((a) => set.add(a));
    });
    return set;
}

export interface RegionPickerProps {
    /** Currently selected region names (mix of super-region + child names). */
    value: string[];
    /** Called whenever the selection changes. */
    onChange: (next: string[]) => void;
    /** Optional section heading rendered above the chips. */
    title?: string;
    /** Primary chip color (active background, outline). Defaults to purple. */
    color?: string;
    /** Darker variant used for hover on active chips. */
    colorDark?: string;
    /** Chip size. Defaults to "small". */
    size?: "small" | "medium";
    /** When true, render chips as read-only (no clicks, no modal, no delete). */
    readOnly?: boolean;
}

export function RegionPicker({
    value,
    onChange,
    title,
    color = "#9c27b0",
    colorDark = "#7b1fa2",
    size = "small",
    readOnly = false,
}: RegionPickerProps) {
    const [openSuper, setOpenSuper] = useState<RegionPickerSuperRegion | null>(
        null,
    );
    // Snapshot of the value at the moment the modal opened, used by Cancel
    // to revert any edits made inside the dialog before Save changes was clicked.
    const snapshotRef = useRef<string[] | null>(null);

    const openSuperModal = (sr: RegionPickerSuperRegion) => {
        snapshotRef.current = value;
        setOpenSuper(sr);
    };

    // Normalize the redundant "all" markers in `value`. The super-region name
    // is kept only when every child is fully selected; a child's own name
    // marker is treated as the authoritative "all members" representation
    // (matching childSubState/collapseChild) and is therefore only ever ADDED,
    // never stripped — so closing the modal with no changes can't silently
    // drop a group-only selection. Called by the Save changes path and any
    // non-Cancel dismissal.
    const normalizeSuperOnClose = (
        sr: RegionPickerSuperRegion,
        currentValue: string[],
    ): string[] => {
        const children = realChildren(sr);
        if (children.length === 0) return currentValue;
        let v = currentValue;
        const inValue = (lower: string) =>
            v.some((x) => x.trim().toLowerCase() === lower);

        // Level 1 — expandable children: ADD the child name marker when every
        // one of its leaf regions is selected and the marker is missing. A
        // marker that's already present stands in for "all members" (the legacy
        // / collapsed form), so it is preserved as-is; partial selections are
        // produced by handleToggleLeaf, which materializes the marker into
        // explicit member names first, so a lingering marker never represents a
        // partial selection here.
        children.forEach((child) => {
            const subs = realSubRegions(child);
            if (subs.length === 0) return;
            if (childAliasesLower(child).some(inValue)) return;
            const allLeaves = subs.every((s) => inValue(leafNameLower(s)));
            if (allLeaves) {
                v = [...v, child.name];
            }
        });

        // Level 2 — super-region: present iff every child is fully selected.
        // After level 1, an expandable child's name marker is present whenever
        // it's fully selected (all leaves, or a preserved legacy marker), so a
        // name check suffices for both plain and expandable children.
        const allChildrenFull = children.every((c) =>
            childAliasesLower(c).some(inValue),
        );
        const superLower = sr.name.toLowerCase();
        const superPresent = inValue(superLower);
        if (allChildrenFull && !superPresent) {
            v = [...v, sr.name];
        } else if (!allChildrenFull && superPresent) {
            v = v.filter((x) => x.trim().toLowerCase() !== superLower);
        }
        return v;
    };

    const closeSuperModal = () => {
        if (openSuper) {
            const normalized = normalizeSuperOnClose(openSuper, value);
            if (normalized !== value) onChange(normalized);
        }
        snapshotRef.current = null;
        setOpenSuper(null);
    };

    const handleDiscardModal = () => {
        if (snapshotRef.current) onChange(snapshotRef.current);
        snapshotRef.current = null;
        setOpenSuper(null);
    };

    // Case-insensitive membership lookup so we tolerate stored values like
    // "north america" or "USA" alongside the canonical display names.
    const valueSetLower = useMemo(
        () => new Set(value.map((v) => v.trim().toLowerCase())),
        [value],
    );

    const hasName = (name: string) =>
        valueSetLower.has(name.trim().toLowerCase());

    // A single leaf region (member state / constituent country) is selected
    // when its canonical name is present in `value`.
    const hasLeaf = (leaf: RegionPickerLeafRegion) =>
        valueSetLower.has(leafNameLower(leaf));

    // The child's own name/abbrev marker is present. For an expandable child
    // this acts as a redundant "all leaves" marker (and also covers legacy
    // values that stored only the group/country name, e.g. "European Union"
    // or "USA", without enumerating each member).
    const childNamePresent = (child: RegionPickerSubRegion) =>
        childAliasesLower(child).some((a) => valueSetLower.has(a));

    // Selection state of an expandable child, derived from its leaves plus the
    // legacy name marker. `all` treats a lone name marker as "every leaf".
    const childSubState = (child: RegionPickerSubRegion) => {
        const subs = realSubRegions(child);
        const namePresent = childNamePresent(child);
        const selected = subs.filter(hasLeaf);
        const all =
            subs.length > 0 &&
            (namePresent || selected.length === subs.length);
        const active = namePresent || selected.length > 0;
        return {
            subs,
            namePresent,
            selected,
            all,
            active,
            partial: active && !all,
        };
    };

    // Child-aware membership: true when the child is selected to ANY degree.
    // For plain children this matches the name/abbrev; for expandable children
    // it's also true when at least one leaf is selected.
    const hasChild = (child: RegionPickerSubRegion) => {
        if (realSubRegions(child).length > 0) return childSubState(child).active;
        return childNamePresent(child);
    };

    // Whether the child is FULLY selected (used for "all children" rollups).
    const childFull = (child: RegionPickerSubRegion) => {
        if (realSubRegions(child).length > 0) return childSubState(child).all;
        return childNamePresent(child);
    };

    // Remove every existing occurrence of `name` (case-insensitive) from value.
    const withoutName = (arr: string[], name: string) =>
        arr.filter((v) => v.trim().toLowerCase() !== name.trim().toLowerCase());

    // Strip every alias of `child` (name + abbrev) so toggling off removes
    // any legacy form that was previously stored.
    const withoutChild = (arr: string[], child: RegionPickerSubRegion) => {
        const aliases = new Set(childAliasesLower(child));
        return arr.filter((v) => !aliases.has(v.trim().toLowerCase()));
    };

    const addName = (arr: string[], name: string) =>
        hasNameIn(arr, name) ? arr : [...arr, name];

    // Add the canonical child name, first stripping any alias form (e.g.
    // replace stored "US" with "USA") so the value never contains both.
    const addChild = (arr: string[], child: RegionPickerSubRegion) => {
        const stripped = withoutChild(arr, child);
        return [...stripped, child.name];
    };

    // Remove a single leaf (member) name from the array.
    const withoutLeaf = (arr: string[], leaf: RegionPickerLeafRegion) =>
        arr.filter((v) => v.trim().toLowerCase() !== leafNameLower(leaf));

    // Strip every leaf name belonging to a child (used when clearing a group).
    const withoutAllLeaves = (arr: string[], child: RegionPickerSubRegion) => {
        const leafSet = new Set(realSubRegions(child).map(leafNameLower));
        return arr.filter((v) => !leafSet.has(v.trim().toLowerCase()));
    };

    // Turn an expandable child's lone name marker into explicit leaf names so a
    // single leaf can then be toggled off. No-op when the marker isn't present.
    const materializeChild = (arr: string[], child: RegionPickerSubRegion) => {
        if (!childNamePresent(child)) return arr;
        let next = withoutChild(arr, child);
        realSubRegions(child).forEach((leaf) => {
            next = addName(next, leaf.name);
        });
        return next;
    };

    const handleToggleSuper = (sr: RegionPickerSuperRegion) => {
        if (readOnly) return;
        const children = realChildren(sr);
        if (children.length === 0) {
            // No real children (e.g. Global) — pure on/off toggle.
            if (hasName(sr.name)) {
                onChange(withoutName(value, sr.name));
            } else {
                onChange([...value, sr.name]);
            }
            return;
        }
        // Has children — clicking always opens the editor modal. The super
        // region's presence in `value` is derived from the child selection
        // when the modal closes, never set directly by the chip click.
        openSuperModal(sr);
    };

    const handleOpenChildren = (sr: RegionPickerSuperRegion) => {
        if (readOnly) return;
        if (realChildren(sr).length === 0) return;
        openSuperModal(sr);
    };

    const handleToggleAll = () => {
        if (!openSuper || readOnly) return;
        const children = realChildren(openSuper);
        const allSelected =
            children.length > 0 && children.every((c) => childFull(c));
        if (allSelected) {
            // Clear: strip each child's name/abbrev AND all of its leaves.
            let next = value;
            children.forEach((c) => {
                next = withoutChild(next, c);
                if (realSubRegions(c).length > 0) {
                    next = withoutAllLeaves(next, c);
                }
            });
            onChange(next);
        } else {
            let next = value;
            children.forEach((c) => {
                if (realSubRegions(c).length > 0) {
                    // Expandable child — select every leaf explicitly.
                    realSubRegions(c).forEach((leaf) => {
                        next = addName(next, leaf.name);
                    });
                } else if (!hasChild(c)) {
                    next = addChild(next, c);
                }
            });
            // Super/child name markers are not added here — `closeSuperModal`
            // normalises them based on whether all descendants are selected.
            onChange(next);
        }
    };

    // Toggle a plain child, or fully select/clear an expandable child group.
    const handleToggleChild = (
        _sr: RegionPickerSuperRegion,
        child: RegionPickerSubRegion,
    ) => {
        if (readOnly) return;
        if (realSubRegions(child).length > 0) {
            const st = childSubState(child);
            if (st.all) {
                onChange(withoutAllLeaves(withoutChild(value, child), child));
            } else {
                let next = withoutChild(value, child);
                realSubRegions(child).forEach((leaf) => {
                    next = addName(next, leaf.name);
                });
                onChange(next);
            }
            return;
        }
        const childActive = hasChild(child);
        const next = childActive
            ? withoutChild(value, child)
            : addChild(value, child);
        // Super-region name is not auto-added here either; the normalize
        // step on modal close decides whether to include it.
        onChange(next);
    };

    // Toggle a single leaf (member) within an expandable child group.
    const handleToggleLeaf = (
        child: RegionPickerSubRegion,
        leaf: RegionPickerLeafRegion,
    ) => {
        if (readOnly) return;
        // A leaf renders as checked when its name is present OR the child's
        // name marker implies "all leaves" (matching `lchecked` in the modal).
        // Base the toggle direction on that displayed state, and expand any
        // lone name marker into explicit leaves first so turning one off
        // leaves the others intact.
        const wasChecked = hasLeaf(leaf) || childNamePresent(child);
        const base = materializeChild(value, child);
        const next = wasChecked
            ? withoutLeaf(base, leaf)
            : addName(base, leaf.name);
        onChange(next);
    };

    const handleRemoveChild = (child: RegionPickerSubRegion) => {
        if (readOnly) return;
        // Removing a single child from the inline list must also remove the
        // super-region name if it was previously present (since the parent
        // is no longer "fully selected" once a child drops out). For an
        // expandable child, drop all of its leaf selections too.
        let stripped = withoutChild(value, child);
        if (realSubRegions(child).length > 0) {
            stripped = withoutAllLeaves(stripped, child);
        }
        // Find which super-region this child belongs to so we can drop the
        // super name if it would otherwise become inconsistent.
        const parent = SUPER_REGIONS.find((sr) =>
            sr.regions.some((r) => r.id === child.id),
        );
        if (parent) {
            onChange(withoutName(stripped, parent.name));
        } else {
            onChange(stripped);
        }
    };

    return (
        <Box>
            {title && (
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5, color }}
                >
                    {title}
                </Typography>
            )}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                }}
            >
                {SUPER_REGIONS.map((sr) => {
                    const superInValue = hasName(sr.name);
                    const children = realChildren(sr);
                    const selectedChildren = children.filter((c) =>
                        hasChild(c),
                    );
                    const expandable = children.length > 0;
                    // The chip is "in use" if either the super-region name
                    // itself is in the value (e.g. just toggled on, or
                    // Global) or any of its children are selected.
                    const active = superInValue || selectedChildren.length > 0;
                    // Fully selected only when EVERY child is fully selected
                    // (an expandable child counts as full only when all of its
                    // leaf members are selected).
                    const allSelected =
                        expandable && children.every((c) => childFull(c));
                    // Partial = active but not fully selected. Renders the
                    // super-region chip as outlined to delineate that it's
                    // acting as an "edit" affordance rather than a standalone
                    // tag — the displayed tags on cards / detail pages will be
                    // the individual sub-region chips shown beneath it.
                    const partial = expandable && active && !allSelected;
                    // Visually filled when fully on (or on with no children
                    // available, e.g. Global). Outlined when partial or off.
                    const displayActive =
                        allSelected || (!expandable && active);
                    // Clicking the chip on an expandable super-region always
                    // opens the editor modal — it does NOT toggle the super
                    // on/off directly. Global (no children) falls back to a
                    // plain on/off toggle via handleToggleSuper.
                    const editOnClick = expandable && !readOnly;
                    // No selection count — with nested groups a single number
                    // can't accurately summarise the selection.
                    const superLabel = sr.name;

                    const superChip = (
                        <Chip
                            label={superLabel}
                            size={size}
                            clickable={!readOnly}
                            variant={displayActive ? "filled" : "outlined"}
                            onClick={
                                readOnly
                                    ? undefined
                                    : editOnClick
                                      ? () => handleOpenChildren(sr)
                                      : () => handleToggleSuper(sr)
                            }
                            sx={{
                                borderColor: color,
                                color: displayActive ? "#fff" : color,
                                backgroundColor: displayActive
                                    ? color
                                    : "transparent",
                                fontWeight: 600,
                                height: "auto",
                                "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    display: "block",
                                    lineHeight: 1.3,
                                    py: "3px",
                                },
                                "&:hover": {
                                    backgroundColor: displayActive
                                        ? colorDark
                                        : "rgba(0,0,0,0.04)",
                                },
                            }}
                        />
                    );

                    return (
                        <Box
                            key={sr.id}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.5,
                            }}
                        >
                            {editOnClick ? (
                                <Tooltip title="Edit region" placement="top">
                                    <span>{superChip}</span>
                                </Tooltip>
                            ) : (
                                superChip
                            )}
                            {partial && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        pl: 1.5,
                                    }}
                                >
                                    {selectedChildren.map((c) => {
                                        return (
                                        <Chip
                                            key={c.id}
                                            label={c.name}
                                            size={size}
                                            variant="filled"
                                            onDelete={
                                                readOnly
                                                    ? undefined
                                                    : () => handleRemoveChild(c)
                                            }
                                            sx={{
                                                backgroundColor: color,
                                                color: "#fff",
                                                fontWeight: 500,
                                                height: "auto",
                                                "& .MuiChip-label": {
                                                    whiteSpace: "normal",
                                                    display: "block",
                                                    lineHeight: 1.3,
                                                    py: "3px",
                                                },
                                                "&:hover": {
                                                    backgroundColor: colorDark,
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                    color: "rgba(255,255,255,0.85)",
                                                    "&:hover": {
                                                        color: "#fff",
                                                    },
                                                },
                                            }}
                                        />
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            <Dialog
                open={!!openSuper}
                onClose={closeSuperModal}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {openSuper ? openSuper.name : ""}
                    <Typography
                        variant="caption"
                        sx={{ display: "block", color: "text.secondary" }}
                    >
                        Toggle the countries/regions that apply.
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.75,
                        }}
                    >
                        {openSuper &&
                            realChildren(openSuper).map((c) => {
                                const leaves = realSubRegions(c);

                                // ── Plain child (no drill-down) ──────────────
                                // A flush-left toggle chip, in a full-width row
                                // so its left edge lines up with the group rows.
                                if (leaves.length === 0) {
                                    const checked = hasChild(c);
                                    return (
                                        <Box key={c.id} sx={{ width: "100%" }}>
                                            <Chip
                                                label={c.name}
                                                clickable={!readOnly}
                                                variant={
                                                    checked
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                onClick={
                                                    readOnly
                                                        ? undefined
                                                        : () =>
                                                              handleToggleChild(
                                                                  openSuper,
                                                                  c,
                                                              )
                                                }
                                                sx={{
                                                    borderColor: color,
                                                    color: checked
                                                        ? "#fff"
                                                        : color,
                                                    backgroundColor: checked
                                                        ? color
                                                        : "transparent",
                                                    fontWeight: 600,
                                                    height: "auto",
                                                    "& .MuiChip-label": {
                                                        whiteSpace: "normal",
                                                        display: "block",
                                                        lineHeight: 1.3,
                                                        py: "3px",
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: checked
                                                            ? colorDark
                                                            : "rgba(0,0,0,0.04)",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    );
                                }

                                // ── Expandable child group ───────────────────
                                // Clicking the group-name chip selects / clears
                                // every member at once. Member countries are
                                // always shown (no expand/collapse control).
                                const st = childSubState(c);
                                return (
                                    <Box key={c.id} sx={{ width: "100%" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                width: "100%",
                                            }}
                                        >
                                            <Chip
                                                label={c.name}
                                                clickable={!readOnly}
                                                onClick={
                                                    readOnly
                                                        ? undefined
                                                        : () =>
                                                              handleToggleChild(
                                                                  openSuper,
                                                                  c,
                                                              )
                                                }
                                                variant={
                                                    st.all
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                sx={{
                                                    borderColor: color,
                                                    color: st.all
                                                        ? "#fff"
                                                        : color,
                                                    backgroundColor: st.all
                                                        ? color
                                                        : st.partial
                                                          ? "rgba(0,0,0,0.04)"
                                                          : "transparent",
                                                    fontWeight: 600,
                                                    height: "auto",
                                                    "& .MuiChip-label": {
                                                        whiteSpace: "normal",
                                                        display: "block",
                                                        lineHeight: 1.3,
                                                        py: "3px",
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: st.all
                                                            ? colorDark
                                                            : "rgba(0,0,0,0.08)",
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            role="group"
                                            aria-label={`${c.name} members`}
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                                pl: 1.5,
                                                pt: 0.5,
                                                pb: 0.5,
                                            }}
                                        >
                                            {leaves.map((leaf) => {
                                                const lchecked =
                                                    hasLeaf(leaf) ||
                                                    st.namePresent;
                                                return (
                                                    <Chip
                                                        key={leaf.id}
                                                        label={leaf.name}
                                                        size="small"
                                                        clickable={!readOnly}
                                                        variant={
                                                            lchecked
                                                                ? "filled"
                                                                : "outlined"
                                                        }
                                                        onClick={
                                                            readOnly
                                                                ? undefined
                                                                : () =>
                                                                      handleToggleLeaf(
                                                                          c,
                                                                          leaf,
                                                                      )
                                                        }
                                                        sx={{
                                                            borderColor: color,
                                                            color: lchecked
                                                                ? "#fff"
                                                                : color,
                                                            backgroundColor:
                                                                lchecked
                                                                    ? color
                                                                    : "transparent",
                                                            fontWeight: 500,
                                                            height: "auto",
                                                            "& .MuiChip-label": {
                                                                whiteSpace:
                                                                    "normal",
                                                                display: "block",
                                                                lineHeight: 1.3,
                                                                py: "3px",
                                                            },
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    lchecked
                                                                        ? colorDark
                                                                        : "rgba(0,0,0,0.04)",
                                                            },
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
                    <Button
                        onClick={handleDiscardModal}
                        sx={{
                            color: "error.main",
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            onClick={handleToggleAll}
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                        >
                            Toggle All
                        </Button>
                        <Button
                            onClick={closeSuperModal}
                            variant="contained"
                            sx={{
                                textTransform: "none",
                                backgroundColor: color,
                                "&:hover": { backgroundColor: colorDark },
                            }}
                        >
                            Save changes
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// Case-insensitive `includes` for the local mutation helpers above. Kept
// outside the component so the closures don't re-create it per render.
function hasNameIn(arr: string[], name: string): boolean {
    const lower = name.trim().toLowerCase();
    return arr.some((v) => v.trim().toLowerCase() === lower);
}

// Suppress unused-import warning when consumers don't yet use NAME_TO_SUPER.
void NAME_TO_SUPER;

// ════════════════════════════════════════════════════════════════════════════
// collapseRegionsForDisplay
// ════════════════════════════════════════════════════════════════════════════
//
// Display helper for cards/detail pages. Given a raw `regionalFocus` list
// (which may mix super-region names, child-region names/abbreviations, and
// leaf member names), return a deduplicated list that collapses each branch
// to the highest fully-selected level:
//   • Every child of a super-region fully selected → emit only the
//     super-region name (children/leaves implied).
//   • Some children selected → drop the super-region name and, per child,
//     emit the child name when it's fully selected or the individual
//     selected leaf names when it's only partially selected.
//   • A child with no leaves behaves as before (name present ⇒ emit name).
// Any input entries that don't belong to a known region (free-form tags) are
// preserved verbatim in their original order. Comparisons are
// case-insensitive and abbreviation-aware (leaves match by name only).
type ChildCollapse = {
    status: "full" | "partial" | "none";
    tags: string[];
    consumed: string[];
};

function collapseChild(
    child: RegionPickerSubRegion,
    valueSetLower: Set<string>,
): ChildCollapse {
    const namePresent = childAliasesLower(child).some((a) =>
        valueSetLower.has(a),
    );
    const leaves = realSubRegions(child);

    if (leaves.length === 0) {
        return namePresent
            ? { status: "full", tags: [child.name], consumed: childAliasesLower(child) }
            : { status: "none", tags: [], consumed: [] };
    }

    const selectedLeaves = leaves.filter((l) =>
        valueSetLower.has(leafNameLower(l)),
    );
    const allLeaves = selectedLeaves.length === leaves.length;

    if (namePresent || allLeaves) {
        return {
            status: "full",
            tags: [child.name],
            consumed: [
                ...childAliasesLower(child),
                ...leaves.map(leafNameLower),
            ],
        };
    }
    if (selectedLeaves.length > 0) {
        return {
            status: "partial",
            tags: selectedLeaves.map((l) => l.name),
            consumed: [
                ...childAliasesLower(child),
                ...selectedLeaves.map(leafNameLower),
            ],
        };
    }
    return { status: "none", tags: [], consumed: [] };
}

export function collapseRegionsForDisplay(value: string[]): string[] {
    if (!value || value.length === 0) return [];
    const valueSetLower = new Set(value.map((v) => v.trim().toLowerCase()));
    const out: string[] = [];
    const consumed = new Set<string>();

    for (const sr of SUPER_REGIONS) {
        const srLower = sr.name.toLowerCase();
        const hasSuper = valueSetLower.has(srLower);
        const children = realChildren(sr);

        if (children.length === 0) {
            if (hasSuper) {
                out.push(sr.name);
                consumed.add(srLower);
            }
            continue;
        }

        const results = children.map((c) => collapseChild(c, valueSetLower));
        const anyActive = results.some((r) => r.status !== "none");
        const allFull = results.every((r) => r.status === "full");

        if (hasSuper) consumed.add(srLower);

        if (!anyActive) {
            // Only the super-region marker (if any) was present.
            if (hasSuper) out.push(sr.name);
            continue;
        }

        if (allFull) {
            // Every child fully selected → collapse to super-region only.
            out.push(sr.name);
            results.forEach((r) => r.consumed.forEach((a) => consumed.add(a)));
            continue;
        }

        // Partial: drop the super-region, emit each child's collapsed tags.
        results.forEach((r) => {
            r.tags.forEach((t) => out.push(t));
            r.consumed.forEach((a) => consumed.add(a));
        });
    }

    // Preserve any leftover (unknown) tags verbatim in original order.
    for (const v of value) {
        if (!consumed.has(v.trim().toLowerCase())) out.push(v);
    }

    // Final case-insensitive dedupe preserving first occurrence.
    const seen = new Set<string>();
    return out.filter((v) => {
        const k = v.trim().toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
}
